import path from 'node:path'
import z from 'zod'

import { ConfigVersion } from '@scalar-org/entities/project'
import { parseAnything } from '@scalar-org/helpers/parse'

import { type ScalarConfigLegacy, scalarConfigSchemaLegacy } from '@/legacy'
import { type ScalarConfig, scalarConfigSchema } from '@/schema'

export type VersionedScalarConfig =
  | (ScalarConfigLegacy & { version: ConfigVersion.V1 })
  | (ScalarConfig & { version: ConfigVersion.V2 })

type ParseConfigResult =
  | { success: true; config: VersionedScalarConfig; error: undefined }
  | {
      success: false
      config?: undefined
      error: unknown
      version?: ConfigVersion
    }

export const configVersionToSchemaMapping = {
  [ConfigVersion.V1]: scalarConfigSchemaLegacy,
  [ConfigVersion.V2]: scalarConfigSchema,
} as const

/** Determines the config version from an unparsed config object */
export function getConfigVersion(config: Record<string, unknown>) {
  if ('scalar' in config) return ConfigVersion.V2

  return ConfigVersion.V1
}

/** Parses a scalar config based on the determined config version */
export function parseConfig(rawConfig: string): ParseConfigResult {
  try {
    const jsonConfigResult = parseAnything(
      rawConfig,
      z.record(z.string(), z.unknown()),
    )

    if (!jsonConfigResult.success)
      return { success: false, error: jsonConfigResult.error }

    // Extract the version from the JSON config
    const version = getConfigVersion(jsonConfigResult.data)

    // Parse the config against the schema for that specific version
    const {
      success,
      data: config,
      error,
    } = configVersionToSchemaMapping[version].safeParse(jsonConfigResult.data)

    if (!success) {
      console.error('Failed to parse config', rawConfig, error)
      return { success: false, error, version }
    }

    return {
      success: true,
      config: {
        ...config,
        version,
      },
    } as ParseConfigResult
  } catch (error) {
    console.error('Failed to parse config', rawConfig, error)
    return { success: false, error }
  }
}

/**
 * Reads & parses the config from the specified dir
 */
export async function getConfigFromDir({
  readFile,
  root,
  configPath,
}: {
  readFile: (path: string) => Promise<string>
  root: string
  configPath: string
}) {
  const rawConfig = await readFile(path.join(root, configPath))

  return parseConfig(rawConfig)
}
